import _ from "lodash";
import { useCallback, useState } from "react";
import { alertError } from "./alerts";
import t from "Utils/translation";

const useFormState = (initialFormState = {}) => {
  const [initialState, setInitialState] = useState(initialFormState);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [forcePristine, setForcePristine] = useState(false);

  const setInitialValues = useCallback((fieldValues) => {
    setFormData(fieldValues);
    setInitialState(fieldValues);
    setForcePristine(false);
  }, []);

  const setFormFieldValue = (fieldsValues) => {
    setForcePristine(false);

    setFormData((prevFormData) => {
      return { ...prevFormData, ...fieldsValues };
    });
  };

  const setFormErrors = (errors, isLoading = false) => {
    setErrors(errors);
    setIsFormLoading(isLoading);
  };

  const resetForm = () => {
    setIsFormLoading(false);
    setFormData({});
    setErrors({});
  };

  const resetToInitialFormState = () => {
    setIsFormLoading(false);
    setFormData(initialFormState);
    setErrors({});
  };

  const resetToInitialState = () => {
    setIsFormLoading(false);
    setFormData(initialState);
    setErrors({});
  };

  const resetFields = (...args) => {
    const fieldsToReset = {};
    args.forEach((field) => (fieldsToReset[field] = initialState[field]));
    setFormData((prevFormData) => ({ ...prevFormData, ...fieldsToReset }));
  };

  const forceFormAsPristine = () => setForcePristine(true);

  const areFieldsPristine = (...args) => {
    if (forcePristine) {
      return true;
    }

    const nonPristineFields = [];

    args.forEach((field) => {
      if (!_.isEqual(initialState[field], formData[field])) {
        nonPristineFields.push(field);
      }
    });

    return nonPristineFields.length === 0;
  };

  const setDataForm = (values) => {
    setIsFormLoading(false);
    setFormData((prevFormData) => ({ ...prevFormData, ...values }));
    setErrors({});
  };

  const handleFormResponseErrors = async (e) => {
    const err = await e.json();

    console.error("qbpanel API error:", err);

    if (e.status >= 500) {
      alertError(t("alert.wrong"));
      return err;
    }

    const formErrors = {};
    if (err.errors) {
      err.errors.forEach((fieldError) => (formErrors[fieldError.field] = fieldError.description));
    }

    if (err.message) {
      formErrors.onForm = err.message;
    }

    setFormErrors(formErrors);

    return err;
  };

  return {
    setDataForm,
    setFormFieldValue,
    areFieldsPristine,
    forceFormAsPristine,
    setInitialValues,
    setFormErrors,
    setFormLoading: () => setIsFormLoading(true),
    unsetFormLoading: () => setIsFormLoading(false),
    resetForm,
    formData,
    formErrors: errors,
    resetFields,
    isFormLoading,
    initialState,
    hasChanged: !_.isEqual(formData, initialFormState),
    hasFormChanged: !_.isEqual(formData, initialState),
    setFormData,
    resetToInitialState,
    resetToInitialFormState,
    handleFormResponseErrors,
  };
};

export default useFormState;
